import type { Catalog } from '@supernova/shared';

/**
 * Estado de "generación" de un catálogo de cara al usuario.
 *
 * Un catálogo recién subido pasa por: uploading → parsing → ready (recursos
 * insertados) → embeddings en background. Aunque `status` ya sea 'ready', el
 * matcher de cotización necesita los EMBEDDINGS para el análisis semántico;
 * usar el catálogo antes degrada el matching a solo-texto. Por eso un catálogo
 * solo es "usable" cuando además terminó de embeber.
 */

/** % de generación [0..99] si está embebiendo con progreso medible; null si no aplica. */
export function catalogGenerationPct(cat: Catalog): number | null {
  if (cat.status !== 'ready') return null; // parsing/uploading: sin % de embeddings aún
  const p = cat.embeddingProgress;
  if (p && p.total > 0 && p.done < p.total) {
    return Math.min(99, Math.round((p.done / p.total) * 100));
  }
  return null;
}

/** True si el catálogo todavía se está generando (parseando o embebiendo). */
export function catalogGenerating(cat: Catalog): boolean {
  if (cat.status === 'failed') return false; // no es "generando" — es error
  if (cat.status !== 'ready') return true;   // uploading/parsing/syncing
  const p = cat.embeddingProgress;
  return !!(p && p.total > 0 && p.done < p.total);
}

/** True si el catálogo está listo para usarse en una cotización (parseado + embebido). */
export function catalogUsable(cat: Catalog): boolean {
  return cat.status === 'ready' && !catalogGenerating(cat);
}

/** Etiqueta de estado legible: "Listo" | "Error" | "Generando catálogo… X%". */
export function catalogStatusLabel(cat: Catalog): string {
  if (cat.status === 'failed') return 'Error';
  if (!catalogGenerating(cat)) return 'Listo';
  const pct = catalogGenerationPct(cat);
  return pct != null ? `Generando catálogo… ${pct}%` : 'Generando catálogo…';
}
